"use client";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Card,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableHead,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import {
  HeaderTableCell,
  TableCell,
  TableContainer,
  TableHeaderRow,
  TableRow,
} from "@/components/molecules/Table/styled";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import useGetUsers from "@/hooks/requests/user/use-get-users";
import useDialog from "@/hooks/use-dialog";
import useSelectSearch from "@/hooks/use-select-search";
import { ICustomerUserServiceQueue } from "@/interfaces/service-queue.interface";
import IUser from "@/interfaces/user.interface";
import { ServiceQueueFormMessages } from "@/messages/service-queue/service-queue-form.messages";
import { FilterType, IFilter } from "@/types/filter.types";
import { UserProfile, UserStatus } from "@/types/user.types";

import {
  MAX_QUEUE_SLA_MINUTES,
  ROUTING_TYPE_OPTIONS,
  serviceQueueFormNames,
} from "./form-data";
import { ServiceQueueFormData } from "./types";

type Props = {
  messages: ServiceQueueFormMessages;
  isLoading: boolean;
  isViewing?: boolean;
};

const USERS_PAGE_NUMBER = 0;
const USERS_PAGE_SIZE = 50;

function ServiceQueueForm({ messages, isLoading, isViewing = false }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const { showToast } = useToast();
  const formik = useFormikContext<ServiceQueueFormData>();
  const { values, errors, submitForm, handleChange, setFieldValue } = formik;
  const isReadOnly = isViewing;

  const routingTypeOptions = ROUTING_TYPE_OPTIONS(messages);
  const selectedRoutingType =
    routingTypeOptions.find((option) => option.value === values.routingType) || null;

  const useSearch = useSelectSearch<IUser>();
  const { isVisible: open, handleClose, handleOpen } = useDialog();
  const [isAddDisabled, setIsAddDisabled] = useState(true);

  const filters = useMemo(
    () =>
      [
        { property: "name", filterType: FilterType.Contains, value: useSearch.search || "" },
        { property: "status", filterType: FilterType.Equals, value: UserStatus.Active },
      ] as IFilter<IUser>[],
    [useSearch.search]
  );

  const { data: usersData, isLoading: isLoadingUsers } = useGetUsers({
    pageNumber: USERS_PAGE_NUMBER,
    pageSize: USERS_PAGE_SIZE,
    filters,
  });

  const attendantUsers = useMemo(() => {
    const items = usersData?.items || [];
    return items.filter((user) =>
      user.userProfile?.some((profile) => profile.profileId === UserProfile.ATTENDANT)
    );
  }, [usersData]);

  const availableUsers = useMemo(() => {
    const selectedIds = new Set((values.users || []).map((user) => user.userID));
    return attendantUsers.filter((user) => !selectedIds.has(user.id));
  }, [attendantUsers, values.users]);

  const noUsersOptionsText =
    !isLoadingUsers && attendantUsers.length === 0
      ? messages.noAttendantsRegistered
      : messages.userNotFound;

  const userExists = useCallback(
    (user: IUser) => (values.users || []).some((item) => item.userID === user.id),
    [values.users]
  );

  useEffect(() => {
    setIsAddDisabled(!useSearch.itemSelected || userExists(useSearch.itemSelected));
  }, [useSearch.itemSelected, userExists]);

  const handleAddUser = () => {
    const selected = useSearch.itemSelected;
    if (!selected || isReadOnly) return;

    if (userExists(selected)) {
      showToast({ text: messages.userAlreadyAdded, type: "error" });
      return;
    }

    const newUser: ICustomerUserServiceQueue = {
      userID: selected.id,
      userName: selected.name,
      active: false,
    };

    setFieldValue(serviceQueueFormNames.users, [...(values.users || []), newUser]);
    useSearch.selectItem(null);
    useSearch.clearSearch();
  };

  const handleRemoveUser = (userID: number) => {
    if (isReadOnly) return;
    const updatedUsers = (values.users || []).filter((user) => user.userID !== userID);
    setFieldValue(serviceQueueFormNames.users, updatedUsers);
  };

  const isUsersLoading = useSearch.isLoading || isLoadingUsers;

  return (
    <MDBox>
      <Card>
        <MDBox p={3}>
          <MDTypography variant="h5">{messages.serviceQueueCardTitle}</MDTypography>
        </MDBox>

        <MDBox component="form" pb={3} px={3}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormField
                placeholder={messages.namePlaceholder}
                fullWidth
                variant="standard"
                label={messages.nameLabel}
                name={serviceQueueFormNames.name}
                onChange={handleChange}
                value={values.name}
                error={!!errors.name}
                helperText={errors.name}
                required
                disabled={isReadOnly}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormField
                fullWidth
                type="number"
                variant="standard"
                label={messages.slaLabel}
                name={serviceQueueFormNames.sla}
                onChange={handleChange}
                value={values.sla}
                error={!!errors.sla}
                helperText={errors.sla || messages.slaHelperText}
                required
                disabled={isReadOnly}
                inputProps={{ min: 1, max: MAX_QUEUE_SLA_MINUTES, step: 1 }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <Autocomplete
                options={routingTypeOptions}
                value={selectedRoutingType}
                disabled={isReadOnly}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, newValue) =>
                  setFieldValue(serviceQueueFormNames.routingType, newValue?.value || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={messages.routingTypeLabel}
                    required
                    error={!!errors.routingType}
                    helperText={errors.routingType}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={!!values.active}
                    disabled={isReadOnly}
                    onChange={(e) => setFieldValue(serviceQueueFormNames.active, e.target.checked)}
                    name={serviceQueueFormNames.active}
                  />
                }
                label={messages.activeLabel}
              />
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Card sx={{ mt: 2 }}>
          <MDBox p={3}>
            <MDTypography variant="h5">{messages.usersCardTitle}</MDTypography>

            {!isReadOnly && (
              <Grid container my={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    open={open}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    value={useSearch.itemSelected}
                    onChange={(_, itemSelected) => useSearch.selectItem(itemSelected)}
                    fullWidth
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name || ""}
                    options={availableUsers}
                    loading={isUsersLoading}
                    noOptionsText={noUsersOptionsText}
                    filterOptions={(options) => options}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => {
                      const { InputProps, ...otherParams } = params;
                      return (
                        <MDInput
                          {...otherParams}
                          label={messages.addUserLabel}
                          variant="standard"
                          onBlur={useSearch.clearSearch}
                          onChange={(e) => useSearch.onSearch(e.target.value)}
                          InputProps={{
                            ...InputProps,
                            endAdornment: (
                              <>
                                {isUsersLoading && (
                                  <CircularProgress color="inherit" sx={{ padding: 0 }} size={20} />
                                )}
                                {InputProps.endAdornment}
                                <IconButton
                                  sx={{ padding: 0 }}
                                  onClick={handleAddUser}
                                  disabled={isAddDisabled || !useSearch.itemSelected}
                                >
                                  <AddIcon />
                                </IconButton>
                              </>
                            ),
                          }}
                        />
                      );
                    }}
                  />
                </Grid>
              </Grid>
            )}

            <TableContainer sx={{ marginTop: 1 }}>
              <Table stickyHeader aria-label="service queue users table">
                <TableHead>
                  <TableHeaderRow>
                    <HeaderTableCell>{messages.userNameColumn}</HeaderTableCell>
                    <HeaderTableCell>{messages.userActiveColumn}</HeaderTableCell>
                    {!isReadOnly && <HeaderTableCell>{messages.optionsColumn}</HeaderTableCell>}
                  </TableHeaderRow>
                </TableHead>
                <TableBody>
                  {(values.users || []).length === 0 ? (
                    <TableRow index={0}>
                      <TableCell colSpan={isReadOnly ? 2 : 3} align="center">
                        <MDTypography variant="h6" color="text" fontWeight="regular">
                          {messages.noUsersAddedMessage}
                        </MDTypography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (values.users || []).map((user, index) => (
                      <TableRow key={`${user.userID}-${index}`} index={index}>
                        <TableCell>{user.userName || "-"}</TableCell>
                        <TableCell>
                          <Switch checked={!!user.active} disabled />
                        </TableCell>
                        {!isReadOnly && (
                          <TableCell>
                            <IconButton onClick={() => handleRemoveUser(user.userID)}>
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </MDBox>
        </Card>

      <MDBox mt={2} display="flex" justifyContent="flex-end" gap={2}>
        <MDButton
          variant="gradient"
          color="light"
          onClick={() => router(ROUTE_URLS.serviceQueue(lang))}
          isLoading={isLoading}
        >
          {isReadOnly ? messages.backButton : messages.cancelButton}
        </MDButton>

        {!isReadOnly && (
          <MDButton variant="gradient" color="dark" onClick={submitForm} isLoading={isLoading}>
            {messages.updateButton}
          </MDButton>
        )}
      </MDBox>
    </MDBox>
  );
}

export default ServiceQueueForm;
